import { hexRadius } from '../../hex/hexRadius';
import type { HexRenderContext } from './hexRenderTypes';
import { drawBiomeOverlay } from './hexBiomeOverlay';
import { mulberry32 } from '../common/mulberry32';
import { hexCenter, hexPath } from '../common/geometry';
import { tileSeedFromPos } from '../common/tileSeed';
import { speckle } from '../common/speckle';

export abstract class AbstractHex {
    render(context: HexRenderContext): void {
        this.drawBase(context);
        this.drawOverlay(context);
        this.drawLabel(context);
    }

    protected drawBase({ ctx, verts, zoom, tile }: HexRenderContext): void {
        const center = hexCenter(verts);
        const rad = hexRadius(center, verts);
        const rng = mulberry32(tileSeedFromPos(tile.position.q, tile.position.r, 0xa11ce));
        const fillStyle = this.getFillColor(tile);

        hexPath(ctx, verts);
        ctx.fillStyle = fillStyle;
        ctx.fill();

        ctx.save();
        ctx.clip();

        const lightX = center.x - rad * 0.75;
        const lightY = center.y - rad * 0.85;
        const shadeX = center.x + rad * 0.65;
        const shadeY = center.y + rad * 0.75;

        ctx.globalCompositeOperation = 'screen';
        const hi = ctx.createRadialGradient(lightX, lightY, rad * 0.05, lightX, lightY, rad * 1.15);
        hi.addColorStop(0, 'rgba(255,255,255,0.16)');
        hi.addColorStop(0.45, 'rgba(255,255,255,0.06)');
        hi.addColorStop(1, 'rgba(255,255,255,0.00)');
        ctx.fillStyle = hi;
        ctx.fillRect(center.x - rad * 1.3, center.y - rad * 1.3, rad * 2.6, rad * 2.6);

        ctx.globalCompositeOperation = 'multiply';
        const sh = ctx.createRadialGradient(shadeX, shadeY, rad * 0.1, shadeX, shadeY, rad * 1.25);
        sh.addColorStop(0, 'rgba(0,0,0,0.14)');
        sh.addColorStop(0.55, 'rgba(0,0,0,0.06)');
        sh.addColorStop(1, 'rgba(0,0,0,0.00)');
        ctx.fillStyle = sh;
        ctx.fillRect(center.x - rad * 1.3, center.y - rad * 1.3, rad * 2.6, rad * 2.6);

        ctx.globalCompositeOperation = 'multiply';
        speckle(ctx, rng, verts, zoom, 0.035);

        ctx.restore();

        hexPath(ctx, verts);
        ctx.lineWidth = 1.15 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.42)';
        ctx.stroke();

        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.lineWidth = 0.85 / zoom;
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.stroke();
        ctx.restore();
    }

    protected drawOverlay(context: HexRenderContext): void {
        drawBiomeOverlay(context);
    }

    protected drawLabel({ ctx, center, zoom, tile }: HexRenderContext): void {
        const letter =
            tile.biome.type === 'water'
                ? tile.biome.waterType === 'lake'
                    ? 'L'
                    : 'O'
                : tile.biome.type[0].toUpperCase();
        ctx.font = `${12 / zoom}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fillText(letter, center.x, center.y);
    }

    protected getFillColor(tile: HexRenderContext['tile']): string {
        const biome = tile.biome.type;
        if (biome === 'water') {
            return tile.biome.waterType === 'lake' ? '#2b517a' : '#1d3557';
        }
        if (biome === 'forest') return '#2e5f3e';
        if (biome === 'mountains') return '#4b4b4b';
        if (biome === 'plains') return '#5f7a3a';
        if (biome === 'hills') return '#6b6b3e';
        if (biome === 'wetlands') return '#3f5f5a';
        if (biome === 'coast') return '#6c8a4e';
        return '#2b3a2b';
    }
}
